/**
 * summaryPdf.js -- renders a stored Annual Client Summary to a PDF.
 *
 * Drawn as text with jsPDF rather than rasterising the dialog: the output stays
 * selectable and small, collapsed accordions still print, and the dialog's
 * scroll container never clips a page. Mirrors the section order of
 * AiClientSummary.js -- change both together.
 *
 * Every page carries the print timestamp in its footer; the first page carries
 * both the generation and print timestamps, because a printed summary outlives
 * the record it was generated from.
 */

import jsPDF from 'jspdf';
import moment from 'moment';

const TIMESTAMP_FORMAT = 'MM/DD/YYYY, hh:mm A';

const PAGE = { format: 'letter', unit: 'pt' };
const MARGIN = 48;
const FOOTER_HEIGHT = 28;

const COLORS = {
    text: [33, 33, 33],
    muted: [110, 110, 110],
    primary: [25, 118, 210],
    warning: [237, 108, 2],
    rule: [210, 210, 210],
    shade: [244, 246, 248],
};

const CONFIDENCE_LABELS = { high: 'High', medium: 'Medium', low: 'Low' };

/* jsPDF's built-in fonts only cover Latin-1. Model output routinely carries
   curly quotes, dashes and ellipses, which would otherwise print as garbage. */
const CHAR_MAP = {
    '‘': "'", '’': "'", '‚': "'", '‛': "'",
    '“': '"', '”': '"', '„': '"',
    '–': '-', '—': '-', '−': '-', '‐': '-', '‑': '-',
    '•': '-', '·': '-',
    '…': '...', ' ': ' ', ' ': ' ', ' ': ' ',
};

const clean = (value) => String(value === null || value === undefined ? '' : value)
    .normalize('NFKC')
    .replace(/[‘-„‐-—−•·…   ]/g, (c) => CHAR_MAP[c] || '')
    .replace(/[^\n\x20-\xFF]/g, '');

const safeFilePart = (value) => clean(value).replace(/[^A-Za-z0-9]+/g, '_').replace(/^_+|_+$/g, '');

/**
 * The charted-dose figures shown under MEDICATIONS & HEALTH, as [label, value] rows.
 * Shared with the on-screen summary so the two can never show different numbers.
 * Nothing when no dose was ever charted: an empty eMAR has no figures worth showing.
 */
export const administrationStats = (admin) => {
    if (!admin || !admin.charted || !admin.expected) return null;
    const pct = (v) => (v === null || v === undefined ? null : `${v}%`);
    return [
        ['Doses charted since', moment(admin.charting_since).format('MM/DD/YYYY')],
        ['Scheduled doses', admin.expected],
        ['Given', admin.taken],
        ['Not given (reason recorded)', admin.not_given],
        ['Not recorded', admin.no_record],
        ['Adherence', pct(admin.adherence_pct)],
        ['Charting coverage', pct(admin.charting_coverage_pct)],
    ];
};

/**
 * @param {Object} payload     the GET /client-summary response (summary, aggregates, generated_at, ...)
 * @param {Object} opts
 * @param {string} opts.customerName
 * @param {string} opts.printedBy   name of the person printing
 * @param {Date|moment} opts.printedAt
 * @param {Function} opts.fieldPathLabel  translates low_confidence_fields paths (shared with the dialog)
 * @param {Object} opts.reasonLabels      suppression reason code -> plain language (shared with the dialog)
 * @returns {jsPDF}
 */
export const buildSummaryPdf = (payload, {
    customerName, printedBy, printedAt, fieldPathLabel = (p) => p, reasonLabels = {},
} = {}) => {
    const doc = new jsPDF({ orientation: 'portrait', ...PAGE });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const contentWidth = pageWidth - MARGIN * 2;
    const bottom = pageHeight - MARGIN - FOOTER_HEIGHT;

    const summary = payload.summary || {};
    const aggregates = payload.aggregates || {};
    const suppression = aggregates.suppression || {};
    const overview = summary.client_overview || {};
    const printedMoment = moment(printedAt);

    let y = MARGIN;

    const setFont = (size, style = 'normal', color = COLORS.text) => {
        doc.setFont('helvetica', style);
        doc.setFontSize(size);
        doc.setTextColor(...color);
    };

    const lineHeight = (size) => size * 1.35;

    const ensureSpace = (height) => {
        if (y + height > bottom) {
            doc.addPage();
            y = MARGIN;
        }
    };

    /* Wraps and writes a paragraph, breaking across pages line by line. */
    const writeText = (text, { size = 10, style = 'normal', color = COLORS.text, indent = 0, after = 4 } = {}) => {
        const value = clean(text).trim();
        if (!value) return;
        setFont(size, style, color);
        const lines = doc.splitTextToSize(value, contentWidth - indent);
        const lh = lineHeight(size);
        lines.forEach((line) => {
            ensureSpace(lh);
            doc.text(line, MARGIN + indent, y + size);
            y += lh;
        });
        y += after;
    };

    const BULLET_INDENT = 14;
    const BULLET_GAP = 1;
    const TITLE_HEIGHT = 6 + lineHeight(10.5) + 6;

    /* Height a paragraph will take, measured with the same wrap writeText uses. */
    const measure = (value, size, indent = 0) => {
        setFont(size);
        return doc.splitTextToSize(clean(value).trim(), contentWidth - indent).length * lineHeight(size);
    };

    /* Splits a bullet into its text and optional sources line; null when empty. */
    const bulletParts = (bullet) => {
        if (!bullet) return null;
        const text = typeof bullet === 'string' ? bullet : bullet.text;
        if (!clean(text).trim()) return null;
        let sourceLine = null;
        if (typeof bullet === 'object') {
            if (bullet.unsourced) {
                sourceLine = { text: 'No source recorded', style: 'italic', color: COLORS.warning };
            } else if (bullet.sources && bullet.sources.length) {
                const labels = bullet.sources.map((s) => s.label || s.ref).filter(Boolean).join('; ');
                sourceLine = { text: `Sources: ${labels}`, style: 'normal', color: COLORS.muted };
            }
        }
        return { text, sourceLine };
    };

    const measureBullet = (parts) => (parts
        ? measure(parts.text, 10, BULLET_INDENT)
            + (parts.sourceLine ? BULLET_GAP + measure(parts.sourceLine.text, 8, BULLET_INDENT) : 0)
        : 0);

    /* `reserve` is the height of whatever must follow the heading on the same
       page, so a heading is never stranded at the foot of a page. */
    const writeSectionTitle = (title, reserve = lineHeight(10) * 2) => {
        ensureSpace(TITLE_HEIGHT + reserve);
        y += 6;
        setFont(10.5, 'bold', COLORS.primary);
        doc.text(clean(title), MARGIN, y + 10.5);
        y += lineHeight(10.5);
        doc.setDrawColor(...COLORS.rule);
        doc.setLineWidth(0.6);
        doc.line(MARGIN, y, pageWidth - MARGIN, y);
        y += 6;
    };

    const writeBullet = (bullet) => {
        const parts = bulletParts(bullet);
        if (!parts) return;

        // Keep a bullet and its sources line on one page.
        ensureSpace(measureBullet(parts));
        doc.setFillColor(...COLORS.text);
        doc.circle(MARGIN + 4, y + 6.5, 1.6, 'F');
        writeText(parts.text, { indent: BULLET_INDENT, after: BULLET_GAP });
        if (parts.sourceLine) {
            const { text, style, color } = parts.sourceLine;
            writeText(text, { size: 8, style, color, indent: BULLET_INDENT, after: 1 });
        }
        y += 4;
    };

    /* Same rule as the dialog's <Section>: nothing at all when it has no content. */
    const writeSection = (title, { headline, bullets, stats } = {}) => {
        const rows = (bullets || []).filter(Boolean);
        const statRows = (stats || []).filter(([, v]) => v !== null && v !== undefined);
        if (!rows.length && !headline && !statRows.length) return;
        const reserve = (headline ? measure(headline, 10) + 4 : 0) + measureBullet(bulletParts(rows[0]));
        writeSectionTitle(title, Math.max(reserve, lineHeight(10)));
        if (headline) writeText(headline, { style: 'bold' });
        rows.forEach(writeBullet);
        if (statRows.length) writeStats(statRows);
        y += 4;
    };

    const writeStats = (rows) => {
        const size = 9;
        const lh = lineHeight(size);
        const pad = 6;
        const height = rows.length * lh + pad * 2;
        // Sized to its content like the dialog's table: a value printed at the far
        // margin is hard to read across to its label. Indented to the bullet text,
        // as in the dialog, so it doesn't line up with the bullet points.
        const left = MARGIN + BULLET_INDENT;
        setFont(size, 'normal');
        const labelWidth = Math.max(...rows.map(([label]) => doc.getTextWidth(clean(label))));
        setFont(size, 'bold');
        const valueWidth = Math.max(...rows.map(([, value]) => doc.getTextWidth(clean(value))));
        const width = Math.min(contentWidth - BULLET_INDENT, labelWidth + 24 + valueWidth + pad * 2);
        ensureSpace(height);
        doc.setFillColor(...COLORS.shade);
        doc.rect(left, y, width, height, 'F');
        let rowY = y + pad;
        rows.forEach(([label, value]) => {
            setFont(size, 'normal', COLORS.muted);
            doc.text(clean(label), left + pad, rowY + size);
            setFont(size, 'bold');
            doc.text(clean(value), left + width - pad, rowY + size, { align: 'right' });
            rowY += lh;
        });
        y += height + 6;
    };

    /* A shaded paragraph. Falls back to plain text if it would not fit on one page. */
    const writeShaded = (text) => {
        const value = clean(text).trim();
        if (!value) return;
        const size = 10;
        const pad = 8;
        setFont(size);
        const lines = doc.splitTextToSize(value, contentWidth - pad * 2);
        const height = lines.length * lineHeight(size) + pad * 2;
        if (height > bottom - MARGIN) {
            writeText(value);
            return;
        }
        ensureSpace(height);
        doc.setFillColor(...COLORS.shade);
        doc.rect(MARGIN, y, contentWidth, height, 'F');
        setFont(size);
        lines.forEach((line, i) => doc.text(line, MARGIN + pad, y + pad + size + i * lineHeight(size)));
        y += height + 10;
    };

    /* ---------------------------------------------------------- title block */

    setFont(18, 'bold');
    doc.text('Annual Client Summary', MARGIN, y + 18);
    y += lineHeight(18);
    if (customerName) writeText(customerName, { size: 12, style: 'bold', after: 0 });
    writeText('Generated by Fieldworker AI. Verify against the record before acting.', { size: 8.5, color: COLORS.muted, after: 10 });

    const generatedLine = [
        `Summary generated: ${moment(payload.generated_at).format(TIMESTAMP_FORMAT)}`,
        payload.generated_by_name ? `by ${payload.generated_by_name}` : null,
    ].filter(Boolean).join(' ');
    const printedLine = [
        `Printed: ${printedMoment.format(TIMESTAMP_FORMAT)}`,
        printedBy ? `by ${printedBy}` : null,
    ].filter(Boolean).join(' ');

    writeText(generatedLine, { size: 9, color: COLORS.muted, after: 1 });
    writeText(printedLine, { size: 9, color: COLORS.muted, after: 1 });
    if (payload.window && payload.window.months) {
        const range = payload.window.start && payload.window.end
            ? ` (${moment(payload.window.start).format('MM/DD/YYYY')} - ${moment(payload.window.end).format('MM/DD/YYYY')})`
            : '';
        const fullYear = suppression.uncapped ? ' - full year, every record read in full' : '';
        writeText(`Covers the last ${payload.window.months} months${range}${fullYear}`, { size: 9, color: COLORS.muted, after: 1 });
    }
    const confidence = payload.hitl && payload.hitl.document_confidence;
    if (confidence) {
        writeText(`Confidence: ${CONFIDENCE_LABELS[confidence] || confidence}`, { size: 9, color: COLORS.muted, after: 1 });
    }
    y += 6;

    if (payload.is_stale) {
        writeText('This summary is more than a week old. The record may have changed since.',
            { size: 8.5, style: 'italic', color: COLORS.warning, after: 2 });
    }
    y += 8;

    /* -------------------------------------------------------------- sections */

    writeShaded(overview.summary);

    const taskOverview = aggregates.task_overview || {};
    const noteOverview = aggregates.note_overview || {};

    writeSection('KEY NEEDS', { bullets: overview.key_needs });
    writeSection('GOALS & PROGRESS', summary.goals_and_progress);
    writeSection('SERVICES DELIVERED', {
        ...(summary.services_delivered || {}),
        stats: taskOverview.total ? [
            ['Tasks in window', taskOverview.total],
            ['Completed', taskOverview.completed],
            ['Open', taskOverview.open],
        ] : null,
    });
    writeSection('ASSESSMENTS & NEEDS', summary.assessments_and_needs);
    writeSection('MEDICATIONS & HEALTH', {
        ...(summary.medications_and_health || {}),
        stats: administrationStats(aggregates.medication_administration),
    });
    writeSection('DOCUMENTATION', {
        ...(summary.documentation_quality || {}),
        stats: noteOverview.total ? [
            ['Case notes', noteOverview.total],
            ['Quality-scored', noteOverview.quality_scored_count],
            ['Average score', noteOverview.quality_score_avg],
        ] : null,
    });
    writeSection('OPEN ITEMS', { bullets: summary.open_items });
    writeSection('WATCH POINTS', { bullets: summary.watch_points });
    writeSection('QUESTIONS FOR THE TEAM', { bullets: summary.questions_for_the_team });

    const lowConfidenceFields = summary.low_confidence_fields || [];
    if (lowConfidenceFields.length) {
        writeSectionTitle(`VERIFY THESE (${lowConfidenceFields.length})`);
        lowConfidenceFields.forEach((f) => {
            const label = fieldPathLabel(f.field_path);
            if (label) writeText(label, { style: 'bold', after: 0 });
            writeText(f.issue, { size: label ? 9 : 10, color: label ? COLORS.muted : COLORS.text, after: 6 });
        });
        y += 4;
    }

    writeSection('DATA GAPS', { bullets: summary.data_gaps });

    if (suppression.notes) {
        writeSectionTitle('WHAT THE SUMMARY IS BASED ON');
        if (suppression.window) {
            writeText(`${suppression.window.start} - ${suppression.window.end}`, { size: 9, after: 4 });
        }
        writeStats([
            ['Case notes read in full', `${suppression.notes.narrated} of ${suppression.notes.total}`],
            suppression.forms ? ['Forms read in full', `${suppression.forms.narrated} of ${suppression.forms.total}`] : null,
            suppression.tasks ? ['Tasks read in detail', `${suppression.tasks.narrated} of ${suppression.tasks.total}`] : null,
        ].filter(Boolean));
        [suppression.notes, suppression.forms, suppression.tasks, suppression.incidents, suppression.medication_administration]
            .filter(Boolean)
            .map((b) => reasonLabels[b.suppression_reason])
            .filter(Boolean)
            .forEach((label) => writeText(label, { size: 9, color: COLORS.muted, after: 1 }));
        writeText(suppression.uncapped
            ? 'Full-year summary: every record in the window was read in full.'
            : 'Everything else was counted, not read. The AI did not see those records.',
            { size: 9, color: COLORS.muted, after: 0 });
    }

    /* ---------------------------------------------------------------- footer */

    const pageCount = doc.getNumberOfPages();
    const footerLeft = clean([
        'Annual Client Summary',
        customerName,
        `Printed ${printedMoment.format(TIMESTAMP_FORMAT)}`,
    ].filter(Boolean).join('  |  '));
    for (let page = 1; page <= pageCount; page += 1) {
        doc.setPage(page);
        const footerY = pageHeight - MARGIN;
        doc.setDrawColor(...COLORS.rule);
        doc.setLineWidth(0.5);
        doc.line(MARGIN, footerY - 14, pageWidth - MARGIN, footerY - 14);
        setFont(7.5, 'normal', COLORS.muted);
        doc.text(footerLeft, MARGIN, footerY);
        doc.text(`Page ${page} of ${pageCount}`, pageWidth - MARGIN, footerY, { align: 'right' });
    }

    return doc;
};

/** Builds the PDF and hands it to the browser as a download. */
export const downloadSummaryPdf = (payload, opts = {}) => {
    const doc = buildSummaryPdf(payload, opts);
    const namePart = safeFilePart(opts.customerName) || 'Client';
    const datePart = moment(payload.generated_at).format('YYYY-MM-DD');
    doc.save(`Annual_Client_Summary_${namePart}_${datePart}.pdf`);
};
